import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { centsToInput, formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import {
  beneficiosDoVinculo,
  fixoDoVinculo,
  type BeneficioCatalogo,
  type LinhaFolhaBeneficio,
} from "@/data/beneficios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  linha: LinhaFolhaBeneficio | null;
  onClose: () => void;
  folhaId: string;
  catalogo: BeneficioCatalogo[];
  onSalvo: () => void;
}

/** Todos os benefícios de um colaborador na folha, inclusive os que vieram dos fixos. */
export const EditarBeneficiosModal = ({ linha, onClose, folhaId, catalogo: todos, onSalvo }: Props) => {
  // Os do vínculo do colaborador, mais qualquer outro que já tenha valor nesta folha.
  const catalogo = useMemo(() => {
    if (!linha) return [];
    const doVinculo = new Set(beneficiosDoVinculo(todos, linha.colaborador.vinculo).map((b) => b.tipo));
    return todos.filter((b) => doVinculo.has(b.tipo) || (linha.valores[b.tipo] ?? 0) > 0);
  }, [todos, linha]);
  const mantiveis = catalogo.filter((b) => !b.sistema && b.ativo);
  const [valores, setValores] = useState<Record<string, string>>({});
  /** Benefícios cujo valor desta folha vira o valor do colaborador nas próximas. */
  const [manter, setManter] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!linha) return;
    setManter(new Set());
    setValores(
      Object.fromEntries(
        catalogo.map((b) => {
          const v = linha.valores[b.tipo] ?? 0;
          return [b.tipo, v > 0 ? centsToInput(v) : ""];
        }),
      ),
    );
  }, [linha, catalogo]);

  const total = useMemo(
    () =>
      Object.values(valores).reduce((s, texto) => {
        try {
          return s + Math.round(parseReaisInput(texto) * 100);
        } catch {
          return s;
        }
      }, 0),
    [valores],
  );

  const alternarManter = (tipo: string, marcado: boolean) =>
    setManter((atual) => {
      const novo = new Set(atual);
      if (marcado) novo.add(tipo);
      else novo.delete(tipo);
      return novo;
    });

  const todosMarcados = mantiveis.length > 0 && manter.size === mantiveis.length;

  const salvar = async () => {
    if (!linha) return;
    const payload: Record<string, number> = {};
    for (const b of catalogo) {
      const texto = valores[b.tipo] ?? "";
      if (!texto.trim()) continue;
      try {
        const reais = parseReaisInput(texto);
        if (reais > 0) payload[b.tipo] = reais;
      } catch {
        toast.error(`Valor inválido em ${b.nome}`);
        return;
      }
    }

    setSalvando(true);
    try {
      await api.put(`/beneficios/folhas/${folhaId}/colaboradores/${linha.colaborador.id}`, {
        valores: payload,
        manter: [...manter],
      });
      toast.success(
        manter.size > 0
          ? `Benefícios atualizados; ${manter.size} mantido(s) nos próximos meses`
          : "Benefícios atualizados",
      );
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!linha} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{linha?.colaborador.name}</DialogTitle>
          <DialogDescription>
            Ajuste qualquer benefício desta folha, inclusive os fixos. Em branco ou zero = não recebe.
            Marque <strong>manter</strong> no benefício que deve repetir esse valor nos próximos meses
            (fica no cadastro do colaborador; se for igual ao fixo, ele volta a seguir o fixo).
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => setManter(todosMarcados ? new Set() : new Set(mantiveis.map((b) => b.tipo)))}
          >
            {todosMarcados ? "Desmarcar todos" : "Manter todos"}
          </Button>
        </div>

        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
          {catalogo.map((b) => {
            const fixo = linha ? fixoDoVinculo(b, linha.colaborador.vinculo) : 0;
            const marcado = manter.has(b.tipo);
            return (
              <div key={b.tipo} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`edit-${b.tipo}`} className="truncate text-xs">
                    {b.nome}
                    {fixo > 0 && (
                      <span className="ml-1 font-normal text-muted-foreground">(fixo {formatCents(fixo)})</span>
                    )}
                  </Label>
                  {b.sistema || !b.ativo ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">{b.sistema ? "do cadastro" : "excluído"}</span>
                  ) : (
                    <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
                      <Checkbox
                        checked={marcado}
                        onCheckedChange={(v) => alternarManter(b.tipo, v === true)}
                        className="h-3.5 w-3.5"
                        aria-label={`Manter ${b.nome} nos próximos meses`}
                      />
                      manter
                    </label>
                  )}
                </div>
                <Input
                  id={`edit-${b.tipo}`}
                  value={valores[b.tipo] ?? ""}
                  onChange={(e) => setValores((v) => ({ ...v, [b.tipo]: maskReaisInput(e.target.value) }))}
                  inputMode="numeric"
                  placeholder="—"
                  className={`h-9 ${marcado ? "border-primary/60" : ""}`}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <p className="text-sm">
            Total: <strong className="tabular-nums">{formatCents(total)}</strong>
            {manter.size > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                · {manter.size} mantido{manter.size === 1 ? "" : "s"} nos próximos meses
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} disabled={salvando}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
